import React, { useEffect, useMemo, useState } from 'react';
import { Switch, Typography, makeStyles } from '@material-ui/core';
import { useSelector } from 'react-redux';
import CustomButton from '../../shared/CustomButton';
import CustomTextField from '../../shared/CustomTextField';
import CustomPopupText from '../../shared/CustomPopupText';
import useAdminUsers from '../hooks/useAdminUsers';
import { SUPER_USER_ROLE } from '../constants';

const useStyles = makeStyles((theme) => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: '520px',
    maxHeight: '74vh',
    overflowY: 'auto',
    paddingRight: '4px',
    '&::-webkit-scrollbar': { width: '8px' },
    '&::-webkit-scrollbar-thumb': { background: '#E4E7EC', borderRadius: '100px' },
  },
  body: { flex: 1 },
  section: {},
  divider: { height: '1px', background: '#F2F4F7', margin: '24px 0' },
  label: {
    fontFamily: 'Lato',
    fontWeight: 600,
    fontSize: '11px',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: '#98A2B3',
    marginBottom: '6px',
  },
  labelRow: { display: 'flex', alignItems: 'baseline', gap: '10px', marginBottom: '12px' },
  readOnlyTag: {
    display: 'inline-flex',
    alignItems: 'center',
    fontFamily: 'Lato',
    fontWeight: 600,
    fontSize: '9px',
    lineHeight: 1,
    letterSpacing: '0.02em',
    color: '#B0B7C3',
    border: '1px solid #EAECF0',
    borderRadius: '4px',
    padding: '3px 5px',
    textTransform: 'none',
  },
  emailValue: {
    fontFamily: 'Lato',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '20px',
    color: '#344054',
    wordBreak: 'break-word',
  },
  sectionTitle: {
    fontFamily: 'Lato',
    fontWeight: 700,
    fontSize: '13px',
    color: '#344054',
    marginBottom: '16px',
  },
  editGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    columnGap: '24px',
  },
  permRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '56px',
  },
  permItem: { display: 'flex', flexDirection: 'column' },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    height: '24px',
    padding: '0 10px',
    borderRadius: '12px',
    fontFamily: 'Lato',
    fontWeight: 700,
    fontSize: '12px',
    lineHeight: '24px',
    alignSelf: 'flex-start',
  },
  chipGreen: { background: '#ECFDF3', color: '#027A48' },
  chipRed: { background: '#FEF3F2', color: '#B42318' },
  chipPurple: { background: '#F4F1FE', color: theme.palette.primary.main },
  chipGrey: { background: '#F2F4F7', color: '#475467' },
  editRow: { display: 'flex', alignItems: 'center', marginLeft: '-8px' },
  editRowLabel: {
    fontFamily: 'Lato',
    fontWeight: 600,
    fontSize: '14px',
    color: '#10182B',
  },
  footer: {
    display: 'flex',
    gap: '12px',
  },
  empty: {
    fontFamily: 'Lato',
    fontWeight: 600,
    fontSize: '16px',
    color: '#667085',
    marginTop: '60px',
    textAlign: 'center',
  },
}));

const isSuperUser = (roles) => Array.isArray(roles) && roles.includes(SUPER_USER_ROLE);

const buildForm = (admin) => ({
  firstName: admin.firstName || '',
  lastName: admin.lastName || '',
  superUser: isSuperUser(admin.roles),
  enabled: !!admin.enabled,
});

const AdminUsersMainContent = () => {
  const classes = useStyles();
  const { updateAdmin } = useAdminUsers();
  const admin = useSelector((state) => state.adminUsersData.selectedAdminDetails) || {};

  const [isEdit, setIsEdit] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    setIsEdit(false);
    setForm(buildForm(admin));
  }, [admin.id]);

  const handleChange = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  // Send only changed fields (the API requires >= 1).
  const changedBody = useMemo(() => {
    const body = {};
    if (form.firstName?.trim() && form.firstName.trim() !== admin.firstName) body.firstName = form.firstName.trim();
    if (form.lastName?.trim() && form.lastName.trim() !== admin.lastName) body.lastName = form.lastName.trim();
    if (form.superUser !== isSuperUser(admin.roles)) body.roles = form.superUser ? [SUPER_USER_ROLE] : [];
    if (form.enabled !== !!admin.enabled) body.enabled = form.enabled;
    return body;
  }, [form, admin]);

  const hasChanges = Object.keys(changedBody).length > 0;

  const handleSave = async () => {
    if (!hasChanges) return;
    const ok = await updateAdmin(admin.id, changedBody);
    if (ok) setIsEdit(false);
  };

  const handleCancel = () => {
    setIsEdit(false);
    setForm(buildForm(admin));
  };

  if (!admin.id) {
    return <Typography className={classes.empty}>Select an admin user to view details.</Typography>;
  }

  return (
    <div className={classes.card}>
      <div className={classes.body}>
        {isEdit && (
          <>
            <div className={classes.section}>
              <div className={classes.editGrid}>
                <div>
                  <CustomPopupText text="First Name" required />
                  <CustomTextField
                    name="firstName"
                    width="100%"
                    value={form.firstName}
                    handleChange={(e) => handleChange('firstName', e.target.value)}
                  />
                </div>
                <div>
                  <CustomPopupText text="Last Name" required />
                  <CustomTextField
                    name="lastName"
                    width="100%"
                    value={form.lastName}
                    handleChange={(e) => handleChange('lastName', e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className={classes.divider} />
          </>
        )}

        <div className={classes.section}>
          <div className={classes.labelRow}>
            <p className={classes.sectionTitle} style={{ marginBottom: 0 }}>
              Email
            </p>
            {!isEdit && <span className={classes.readOnlyTag}>Read-only</span>}
          </div>
          {isEdit ? (
            <CustomTextField name="email" width="100%" value={admin.email || ''} disabled />
          ) : (
            <p className={classes.emailValue}>{admin.email || '--'}</p>
          )}
        </div>

        <div className={classes.divider} />

        <div className={classes.section}>
          <p className={classes.sectionTitle}>Permissions</p>
          <div className={classes.permRow}>
            <div className={classes.permItem}>
              <p className={classes.label}>Status</p>
              {isEdit ? (
                <div className={classes.editRow}>
                  <Switch
                    color="primary"
                    checked={!!form.enabled}
                    inputProps={{ 'aria-label': 'Status' }}
                    onChange={(e) => handleChange('enabled', e.target.checked)}
                  />
                  <span className={classes.editRowLabel}>{form.enabled ? 'Enabled' : 'Disabled'}</span>
                </div>
              ) : (
                <span className={`${classes.chip} ${admin.enabled ? classes.chipGreen : classes.chipRed}`}>
                  {admin.enabled ? 'Enabled' : 'Disabled'}
                </span>
              )}
            </div>
            <div className={classes.permItem}>
              <p className={classes.label}>Super Admin</p>
              {isEdit ? (
                <div className={classes.editRow}>
                  <Switch
                    color="primary"
                    checked={!!form.superUser}
                    inputProps={{ 'aria-label': 'Super Admin' }}
                    onChange={(e) => handleChange('superUser', e.target.checked)}
                  />
                  <span className={classes.editRowLabel}>{form.superUser ? 'Yes' : 'No'}</span>
                </div>
              ) : (
                <span className={`${classes.chip} ${isSuperUser(admin.roles) ? classes.chipPurple : classes.chipGrey}`}>
                  {isSuperUser(admin.roles) ? 'Yes' : 'No'}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className={classes.divider} />

        <div className={classes.footer}>
          {isEdit ? (
            <>
              <CustomButton label="Cancel" variant="contained" size="large" type="secondary" onClick={handleCancel} />
              <CustomButton
                label="Save"
                variant="contained"
                size="large"
                type="primary"
                disabled={!hasChanges}
                onClick={handleSave}
              />
            </>
          ) : (
            <CustomButton label="Edit" variant="outlined" size="large" type="primary" onClick={() => setIsEdit(true)} />
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminUsersMainContent;
